import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { endPoints } from "../../../network/constant";
import axiosInstance from "../../../network/https";

// Async thunks for API calls
export const fetchNotifications = createAsyncThunk(
  "notifications/fetchNotifications",
  async ({ page = 1, pageSize = 20 } = {}, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(endPoints.Notifications, {
        params: { page, page_size: pageSize },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch notifications");
    }
  }
);

export const fetchNotificationStats = createAsyncThunk("notifications/fetchStats", async (_, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get(endPoints.NotificationStats);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || "Failed to fetch notification stats");
  }
});

export const fetchNotificationPreferences = createAsyncThunk(
  "notifications/fetchPreferences",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(endPoints.NotificationPreferences);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch preferences");
    }
  }
);

export const updateNotificationPreferences = createAsyncThunk(
  "notifications/updatePreferences",
  async (preferences, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(endPoints.UpdateNotificationPreferences, preferences);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to update preferences");
    }
  }
);

// REST mark-as-read removed; handled via WebSocket 'mark_seen_messages'

const initialState = {
  notifications: [],
  stats: { count: 0 },
  preferences: { chat_delay_minutes: 60 },
  loading: false,
  error: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    hasNext: false,
    hasPrevious: false,
  },
};

const notificationSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetNotifications: (state) => {
      state.notifications = [];
      state.pagination = initialState.pagination;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch notifications
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading = false;
        const {
          notifications,
          total_count: totalCount,
          page: currentPage,
          page_size: pageSize,
          has_next: hasNext,
          has_previous: hasPrevious,
        } = action.payload;
        const requestedPage = action.meta.arg?.page || 1;
        const totalPages = Math.max(1, Math.ceil(totalCount / (pageSize || 1)));

        if (requestedPage === 1) {
          state.notifications = notifications || [];
        } else {
          state.notifications = [...state.notifications, ...(notifications || [])];
        }

        state.pagination = {
          currentPage: currentPage || 1,
          totalPages,
          totalCount: totalCount || 0,
          hasNext: !!hasNext,
          hasPrevious: !!hasPrevious,
        };
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch stats
      .addCase(fetchNotificationStats.fulfilled, (state, action) => {
        state.stats = action.payload;
      })
      .addCase(fetchNotificationStats.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Fetch preferences
      .addCase(fetchNotificationPreferences.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotificationPreferences.fulfilled, (state, action) => {
        state.loading = false;
        state.preferences = action.payload;
      })
      .addCase(fetchNotificationPreferences.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update preferences
      .addCase(updateNotificationPreferences.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateNotificationPreferences.fulfilled, (state, action) => {
        state.loading = false;
        // Backend returns a message; persist the submitted preferences locally
        state.preferences = action.meta?.arg ?? state.preferences;
      })
      .addCase(updateNotificationPreferences.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // No REST-based mark-as-read (handled via WebSocket)
  },
});

export const { clearError, resetNotifications } = notificationSlice.actions;

export default notificationSlice.reducer;
